'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerTitle, DrawerTrigger } from '@achra/ui/drawer';
import { cn } from '@achra/ui/lib/utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { BrushCleaning, CheckIcon, FilterIcon, Loader2 } from 'lucide-react';
interface FilterDrawerProps extends React.PropsWithChildren {
  filterTrigger?: React.ReactNode;
  onReset?: () => void;
  className?: string;
  isResetDisabled?: boolean;
  isResetPending?: boolean;
}
function FilterDrawer(t0) {
  const $ = _c(21);
  const {
    children,
    onReset,
    filterTrigger,
    className,
    isResetDisabled,
    isResetPending
  } = t0;
  let t1;
  if ($[0] !== filterTrigger) {
    t1 = filterTrigger ?? <Button variant="outline" size="icon"><FilterIcon /></Button>;
    $[0] = filterTrigger;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <DrawerTrigger asChild={true}>{t1}</DrawerTrigger>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <VisuallyHidden><DrawerTitle>Filters</DrawerTitle></VisuallyHidden>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = typeof onReset !== "function";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("mx-auto flex w-full max-w-150 flex-col gap-4 overflow-y-auto px-4", {
      "pb-8": t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== children || $[8] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[7] = children;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isResetDisabled || $[11] !== isResetPending || $[12] !== onReset) {
    t7 = typeof onReset === "function" && <DrawerFooter className="pt-4 pb-8"><div className="mx-auto w-full max-w-150"><Button variant="secondary" onClick={onReset} className="w-full" disabled={isResetDisabled ?? isResetPending}>{isResetPending ? <Loader2 className="animate-spin" /> : <BrushCleaning />} Reset</Button></div></DrawerFooter>;
    $[10] = isResetDisabled;
    $[11] = isResetPending;
    $[12] = onReset;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== className || $[15] !== t6 || $[16] !== t7) {
    t8 = <DrawerContent className={className}>{t3}{t6}{t7}</DrawerContent>;
    $[14] = className;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t2 || $[19] !== t8) {
    t9 = <Drawer>{t2}{t8}</Drawer>;
    $[18] = t2;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
interface DrawerSelectProps<T extends string> {
  value?: T | T[];
  onChange: ((value: T) => void) | ((values: T[]) => void);
  label?: string;
  options: Array<{
    label: string | React.ReactNode;
    value: T;
  }>;
  multiselect?: boolean;
  /** Enable or disable the "Select All" functionality. Default is false. */
  enableSelectAll?: boolean;
  /** Label for the "Select All" option. Used when enableSelectAll is true. */
  selectAllLabel?: string;
}
function DrawerSelect(t0) {
  const $ = _c(32);
  const {
    value,
    onChange,
    label,
    options,
    multiselect: t1,
    enableSelectAll: t2,
    selectAllLabel: t3
  } = t0;
  const multiselect = t1 === undefined ? false : t1;
  const enableSelectAll = t2 === undefined ? false : t2;
  const selectAllLabel = t3 === undefined ? "Select All" : t3;
  let t4;
  if ($[0] !== multiselect || $[1] !== onChange || $[2] !== value) {
    t4 = optionValue => {
      if (multiselect && Array.isArray(value)) {
        const isSelected = value.includes(optionValue);
        if (isSelected) {
          (onChange as (values: T[]) => void)(value.filter(val => val !== optionValue));
        } else {
          (onChange as (values: T[]) => void)([...value, optionValue]);
        }
      } else {
        if (!multiselect && !Array.isArray(value)) {
          (onChange as (value: T) => void)(optionValue);
        }
      }
    };
    $[0] = multiselect;
    $[1] = onChange;
    $[2] = value;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleToggle = t4;
  let t5;
  if ($[4] !== multiselect || $[5] !== value) {
    t5 = optionValue_0 => {
      if (multiselect && Array.isArray(value)) {
        return value.includes(optionValue_0);
      }
      return value === optionValue_0;
    };
    $[4] = multiselect;
    $[5] = value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isSelected_0 = t5;
  const allOptionsSelected = multiselect && Array.isArray(value) && value.length === options.length;
  let t6;
  if ($[7] !== allOptionsSelected || $[8] !== multiselect || $[9] !== onChange || $[10] !== options || $[11] !== value) {
    t6 = () => {
      if (!multiselect || !Array.isArray(value)) {
        return;
      }
      if (allOptionsSelected) {
        (onChange as (values: T[]) => void)([]);
      } else {
        const allValues = options.map(_temp);
        (onChange as (values: T[]) => void)(allValues);
      }
    };
    $[7] = allOptionsSelected;
    $[8] = multiselect;
    $[9] = onChange;
    $[10] = options;
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSelectAll = t6;
  let t7;
  if ($[13] !== label) {
    t7 = <div className="px-8 py-2 text-sm/5.5 font-semibold">{label}</div>;
    $[13] = label;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== allOptionsSelected || $[16] !== enableSelectAll || $[17] !== handleSelectAll || $[18] !== multiselect || $[19] !== selectAllLabel) {
    t8 = enableSelectAll && multiselect && <div className="hover:bg-accent flex cursor-pointer items-center justify-between py-2 pr-3 pl-8 text-sm/5.5 font-medium" onClick={handleSelectAll}>{selectAllLabel}{allOptionsSelected && <CheckIcon className="ml-auto size-4" />}</div>;
    $[15] = allOptionsSelected;
    $[16] = enableSelectAll;
    $[17] = handleSelectAll;
    $[18] = multiselect;
    $[19] = selectAllLabel;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== handleToggle || $[22] !== isSelected_0 || $[23] !== options) {
    let t10;
    if ($[25] !== handleToggle || $[26] !== isSelected_0) {
      t10 = option_0 => <div key={option_0.value} className="hover:bg-accent flex cursor-pointer items-center justify-between py-2 pr-3 pl-8 text-sm/5.5" onClick={() => {
        handleToggle(option_0.value);
      }}>{option_0.label}{isSelected_0(option_0.value) && <CheckIcon className="ml-auto size-4" />}</div>;
      $[25] = handleToggle;
      $[26] = isSelected_0;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t9 = options.map(t10);
    $[21] = handleToggle;
    $[22] = isSelected_0;
    $[23] = options;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className="bg-popover flex flex-col rounded-md border py-1">{t7}{t8}{t9}</div>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp(option) {
  return option.value;
}
export { FilterDrawer, DrawerSelect };